// @ts-nocheck
import { difference, isEmpty } from 'lodash';
import { GeoDictionaryTagsConfig } from 'mercator-cartography-api-client/lib/interfaces/geoDictionary.types';
import React from 'react';
import ReactDOM from 'react-dom';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { RadiusCards, VariableSelector } from 'sonata-platform-components';
import {
  ButtonUI,
  CustomThemeProvider,
  Input,
  LoadingSkeleton,
  RadioButtonsGroup,
  Selector,
  type SelectorOptionPropsInterface,
  StepsWizardFooter,
  showNotification,
  usePortal,
  useQueryFetch
} from 'sonata-react-components';
import { normalizeLiVariables } from 'sonata-static-storage/lib/helpers/variables.helpers';

import { setCoverageStudyReportStatusChanged } from '@/store/actions/reporting.actions';
import { CoverageStudySectionStyle } from './CoverageStudySection.style';
import { sortAudienceVariablesArrayByOrder } from './helpers';
import { generateCoverageStudyReport, getCartographyDictionary, getCoverageReportVariables } from './resources';

import type { UserInterface } from 'sonata-static-storage/lib/schemas/user/user.schema';
import type { UserRoleInterface } from 'sonata-static-storage/lib/schemas/user/userRole.schema';
import type { CampaignItem } from '@/ts-modules/reporting/CampaignListSelector/CampaignListSelector';

type Props = {
  campaigns: CampaignItem[];
  onBackClick: () => void;
  user: UserInterface;
  userRoles: UserRoleInterface[];
};

type State = {
  name: string;
  selectedVariables: string[];
  radius?: number;
  universeLevel?: string;
  viewType: 'detail' | 'circuit';
  isRequestInProgress: boolean;
};

export default function CoverageStudySection({ campaigns, onBackClick, user, userRoles }: Readonly<Props>) {
  const targetBody = usePortal('coverageStudySection');
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const [{ isRequestInProgress, name, universeLevel, radius, viewType, selectedVariables }, setState] =
    React.useState<State>({
      isRequestInProgress: false,
      name: `${campaigns.map(({ id }) => id).join('-')}_DooHCoverageStudy_${new Date().toISOString().slice(0, 10)}`,
      radius: 100,
      selectedVariables: [],
      universeLevel: '',
      viewType: 'detail'
    });

  // Coverage variables and the universe level dictionary are geographic, not per-campaign,
  // so all selected campaigns are expected to share the same market.
  const country = campaigns[0].countries[0].toUpperCase();
  const baseId = 'reporting-coverageStudy';

  const tokens = {
    dictionary: {},
    generate: {},
    variables: {}
  };

  const { data: variables, isLoading: isLoadingGetVariables } = useQueryFetch([country, user], async () => {
    const allVariables = await getCoverageReportVariables(country, tokens.variables);
    const normalizedAudienceVariables = normalizeLiVariables(allVariables || {});
    return Object.keys(normalizedAudienceVariables.levels).reduce((acc, levelKey) => {
      const currentLevelItems = normalizedAudienceVariables.levels[levelKey];
      const sortedItemsForThisLevel = sortAudienceVariablesArrayByOrder(
        currentLevelItems as unknown as Record<string, number>[]
      );

      return {
        ...acc,
        [levelKey]: sortedItemsForThisLevel
      };
    }, {});
  });

  const { data: dictionary, isLoading: isLoadingGetDictionary } = useQueryFetch([country, userRoles], async () => {
    const dictionaries = await getCartographyDictionary([country], tokens.dictionary);
    return dictionaries[0];
  });

  const universeLevelOptions: SelectorOptionPropsInterface[] = dictionary
    ? (dictionary.tagsConfig as GeoDictionaryTagsConfig[])
        .filter(tag => tag.universeLevel)
        .map(tag => ({
          id: tag.id as string,
          label: tag.name as string
        }))
    : [];

  const handleFieldChange = (value: string | number, field: keyof Omit<State, 'selectedVariables'>) => {
    setState(prevState => ({
      ...prevState,
      [field]: value
    }));
  };

  const handleVariablesChange = (items: string[]) => {
    if (items.length !== selectedVariables.length || difference(selectedVariables, items).length) {
      setState(prevState => ({
        ...prevState,
        selectedVariables: items
      }));
    }
  };

  const handleGenerate = async () => {
    try {
      setState(prevState => ({
        ...prevState,
        isRequestInProgress: true
      }));
      const report = await generateCoverageStudyReport(
        {
          campaigns: campaigns.map(({ id }) => id),
          name,
          output: viewType,
          radius,
          source: 'reporting',
          universeLevel,
          variables: selectedVariables
        },
        tokens.generate
      );
      dispatch(
        setCoverageStudyReportStatusChanged({
          name: report.name,
          reportId: report.reportId,
          reportUrl: '',
          status: report.status
        })
      );
      onBackClick();
    } catch (error) {
      // biome-ignore lint/suspicious/noConsole: off
      console.error('Error generating coverage study report:', error);
      showNotification(
        'alert',
        formatMessage({
          defaultMessage: 'There was an error while preparing the study report.',
          id: 'reportingCoverageStudy.generationError'
        })
      );
    } finally {
      setState(prevState => ({
        ...prevState,
        isRequestInProgress: false
      }));
    }
  };

  const viewTypes = [
    {
      id: 'detail',
      text: formatMessage({ defaultMessage: 'Detailed', id: 'reportingCoverageStudy.viewSettings.detailed' })
    },
    {
      id: 'circuit',
      text: formatMessage({ defaultMessage: 'Circuit', id: 'reportingCoverageStudy.viewSettings.circuit' })
    }
  ];

  const variableSelectorOptions = [
    {
      id: 'level2',
      label: formatMessage({ defaultMessage: 'Variables', id: 'reportingCoverageStudy.variables' })
    }
  ];

  const isExportButtonDisabled = name?.length < 3 || selectedVariables.length === 0 || !universeLevel;

  return ReactDOM.createPortal(
    <CustomThemeProvider themeContrast="light">
      <CoverageStudySectionStyle className="coverageStudySection">
        <div className="coverageStudySection__header">
          <div className="coverageStudySection__headerContent">
            <button className="coverageStudySection__headerContentBack" onClick={onBackClick} type="button">
              <i className="fal fa-arrow-left" />
            </button>
            <span className="coverageStudySection__headerContentMessage">
              {formatMessage({
                defaultMessage: 'Coverage Study',
                id: 'reportingCoverageStudy.title'
              })}
            </span>
          </div>
        </div>
        <div className="coverageStudySection__main">
          <div className="coverageStudySection__mainGeneral">
            <span className="coverageStudySection__sectionTitle">
              {formatMessage({
                defaultMessage: 'General Settings',
                id: 'reportingCoverageStudy.generalSettins'
              })}
            </span>
            <div className="coverageStudySection__mainGeneralContent">
              <Input
                className="coverageStudySection__mainGeneralName"
                id={`${baseId}-name`}
                label={formatMessage({
                  defaultMessage: 'Export Name',
                  id: 'reportingCoverageStudy.name'
                })}
                name="coverageStudy-name"
                onChange={value => handleFieldChange(value as string, 'name')}
                required
                shape="squareUI"
                type="text"
                value={name}
              />
              <Selector
                className="coverageStudySection__mainGeneralLevel"
                id={`${baseId}-level`}
                isDisabled={isLoadingGetDictionary || isEmpty(dictionary)}
                label={formatMessage({
                  defaultMessage: 'Universe Level',
                  id: 'reportingCoverageStudy.level'
                })}
                onChange={(value: Record<string, string>) => handleFieldChange(value.id as string, 'universeLevel')}
                options={universeLevelOptions}
                tooltipText={formatMessage({
                  defaultMessage:
                    'This selector defines the geographic reference level that will be used for the analysis. The system will compare the data of each unit within the selected level against the overall of that same level.',
                  id: 'reportingCoverageStudy.level.tooltip'
                })}
                type="simple"
                value={universeLevelOptions.filter(option => option.id === universeLevel)}
              />
            </div>
          </div>
          <div className="coverageStudySection__mainView">
            <span className="coverageStudySection__sectionTitle">
              {formatMessage({
                defaultMessage: 'View Settings',
                id: 'reportingCoverageStudy.viewSettings'
              })}
            </span>
            <div className="coverageStudySection__mainViewContent">
              <span className="coverageStudySection__mainViewDescription">
                {formatMessage({
                  defaultMessage: 'Choose between a detailed view or a simplified circuit view of your export.',
                  id: 'reportingCoverageStudy.viewSettings.description'
                })}
              </span>
              <RadioButtonsGroup
                className="coverageStudySection__mainViewType"
                id={`${baseId}-viewType`}
                items={viewTypes}
                onChange={value => handleFieldChange(value, 'viewType')}
                tiny
                value={viewType}
              />
            </div>
          </div>
          <div className="coverageStudySection__mainVariables">
            <span className="coverageStudySection__sectionTitle">
              {formatMessage({
                defaultMessage: 'Variables',
                id: 'reportingCoverageStudy.variables'
              })}
            </span>
            <div className="coverageStudySection__mainVariablesContent">
              {isLoadingGetVariables ? (
                <LoadingSkeleton count={1} height={250} />
              ) : (
                <VariableSelector
                  idsSelectedItems={selectedVariables}
                  items={variables}
                  levelToExclude="level0"
                  onChange={handleVariablesChange}
                  options={variableSelectorOptions}
                />
              )}
            </div>
          </div>
          <div className="coverageStudySection__mainRadius">
            <span className="coverageStudySection__sectionTitle">
              {formatMessage({
                defaultMessage: 'Screen Radius',
                id: 'reportingCoverageStudy.screenRadius'
              })}
            </span>
            <div className="coverageStudySection__mainRadiusContent">
              <RadiusCards
                cardWidth={140}
                onSelectionChange={(radius: number) => handleFieldChange(radius, 'radius')}
              />
            </div>
          </div>
          <div className="coverageStudySection__mainButtons">
            <StepsWizardFooter
              leftButton={
                <ButtonUI
                  id={`${baseId}-cancelBtn`}
                  onClick={onBackClick}
                  shape="link"
                  text={formatMessage({
                    defaultMessage: 'Cancel',
                    id: 'reportingCoverageStudy.buttons.cancel'
                  })}
                  type="secondary"
                />
              }
              rightButton={
                <ButtonUI
                  disabled={isExportButtonDisabled}
                  icon="far fa-arrow-right"
                  iconPosition="right"
                  id={`${baseId}-exportBtn`}
                  loading={isRequestInProgress}
                  onClick={handleGenerate}
                  shape="squareRounded"
                  text={formatMessage({
                    defaultMessage: 'Export',
                    id: 'reportingCoverageStudy.buttons.export'
                  })}
                />
              }
              totalSteps={1}
            />
          </div>
        </div>
        <div className="coverageStudySection__footer" />
      </CoverageStudySectionStyle>
    </CustomThemeProvider>,
    targetBody
  );
}
